/**
 * ScrollArea Component
 *
 * A scrollable container for rich text content. When scrollToBottom is true,
 * automatically scrolls to the bottom on mount and when content changes
 * (e.g. for conversation history so latest messages are visible).
 */

import React, { useRef, useEffect } from 'react';

export interface ScrollAreaProps {
  children: React.ReactNode;
  maxHeight?: string;
  scrollToBottom?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const ScrollArea: React.FC<ScrollAreaProps> = ({
  children,
  maxHeight,
  scrollToBottom = false,
  className = '',
  'data-testid': dataTestId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollToBottom || !containerRef.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [scrollToBottom, children]);

  return (
    <div
      ref={containerRef}
      className={`rich-text-scroll-area rounded-xl ${className}`}
      style={{
        overflowY: 'auto',
        ...(maxHeight ? { maxHeight } : {}),
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.75rem',
        padding: '0.75rem 1rem 0.75rem 0.5rem',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
      }}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
};

export default ScrollArea;
